import { useState } from 'react';
import { ChatInterface } from '../../components/ChatInterface';
import { QuotePreview } from '../../components/QuotePreview';

interface GeneratedQuote {
  title: string;
  customer: string;
  totalPrice: number;
  lineItems: Array<{
    description: string;
    price: number;
    quantity: number;
    discount?: number;
  }>;
}

import { useQuote } from '../../contexts/QuoteContext';

export const CreateQuote = ({ user, onNavigate, onQuoteCreated }: { user: any; onNavigate: (page: string) => void; onQuoteCreated: (quoteId: string) => void }) => {
  const [generatedQuote, setGeneratedQuote] = useState<GeneratedQuote | null>(null);
  const [chatMessages, setChatMessages] = useState<Array<{role: 'user' | 'assistant', content: string}>>([]);
  
  const { 
    currentQuote,
    isGenerating,
    isSaving,
    responseId,
    setResponseId,
    generateQuote,
    saveQuote
  } = useQuote();

  const handleChatSubmit = async (message: string) => {
    // Add user message to chat
    setChatMessages(prev => [...prev, { role: 'user', content: message }]);
    
    try {
      const result = await generateQuote(message, responseId || undefined);
      
      // Set the generated quote for display
      setGeneratedQuote(result.response);
      
      // Add assistant response to chat
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Quote generated successfully! Please review below.' 
      }]);
    } catch (error) {
      alert('Failed to generate quote: ' + error);
    }
  };

  const handleSaveQuote = async () => {
    if (!currentQuote) return;
    
    try {
      const savedQuoteData = await saveQuote();
      console.log('Saved quote response:', savedQuoteData);
      
      // Set the quote to be selected when we navigate to quotes page
      if (savedQuoteData && savedQuoteData._id) {
        console.log('Setting selected quote ID:', savedQuoteData._id);
        onQuoteCreated(savedQuoteData._id);
      } else {
        console.log('No _id found in saved quote data');
      }
      
      // Navigate to quotes page
      onNavigate('quotes');
    } catch (error) {
      alert('Failed to save quote: ' + error);
    }
  };

  const handleStartOver = () => {
    setGeneratedQuote(null);
    setResponseId(null);
    setChatMessages([]);
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1>Create Quote</h1>
        <button onClick={() => onNavigate('quotes')} className="ghost-button">
          ← Back to Quotes
        </button>
      </div>

      <div className="create-quote-container">
        <div className="create-quote-layout">
          {/* Left Side - Chat */}
          <div className="chat-section">
            <div className="chat-intro">
              <h2>Describe your quote</h2>
              <p>Describe your quote requirements to build a quote in seconds.</p>
              {chatMessages.length === 0 && (
                <div className="example-prompts">
                  <p><strong>Examples:</strong></p>
                  <ul>
                    <li>"Enterprise software license for Acme Corp, 100 users at $500 per user"</li>
                    <li>"Consulting services for TechStart Inc, 3 months at $10,000 per month"</li>
                    <li>"Web development project for Local Business, design and development phases"</li>
                  </ul>
                </div>
              )}
            </div>
            
            {/* Chat History */}
            {chatMessages.length > 0 && (
              <div className="chat-history">
                {chatMessages.map((msg, index) => (
                  <div key={index} className={`chat-message ${msg.role}`}>
                    <strong>{msg.role === 'user' ? 'You' : 'Assistant'}:</strong> {msg.content}
                  </div>
                ))}
              </div>
            )}
            
            <ChatInterface 
              onSubmit={handleChatSubmit}
              isLoading={isGenerating}
              placeholder={generatedQuote ? "Ask for changes or refinements..." : "Describe the quote you want to create..."}
            />
          </div>

          {/* Right Side - Quote Preview */}
          <div className="preview-section">
            {generatedQuote ? (
              <QuotePreview 
                quote={generatedQuote}
                onSave={handleSaveQuote}
                onStartOver={handleStartOver}
                isSaving={isSaving}
              />
            ) : (
              <div className="preview-placeholder">
                <div className="placeholder-content">
                  <div className="placeholder-icon">💬</div>
                  <h3>Quote Preview</h3>
                  <p>Your generated quote will appear here after you describe what you need.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};